import {
  ApplicationOccupantDto,
  ApplicationPetDto,
  InvitedApplicationBasicDto,
  ApplicationDto,
  ApplicationSearchResponseDto,
  TermsAndConditionsDto,
  ApplicationVehicleDto,
  InviteCoapplicantResponseDto,
  ApplicationEmploymentDto,
  ApplicationRentalHistoryDto,
  ScreeningOrderDto,
  ApplicationTsCsDto,
  ApplicationV2Dto,
  ApplicationSummaryDto,
  ApplicationSearchResponseV3Dto,
  ApplicationNoteDto,
  ApplicationStatusChangeResponseDto,
} from '@inosio/payments-sdk';
import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../../app/store';

interface ApplicationsDataPage {
  Rows: Array<ApplicationDto>;
}

interface ApplicationsDataV2Page {
  Rows: ApplicationSummaryDto[];
}

type ApplicationsState = {
  application: ApplicationV2Dto;
  isGettingApplication: boolean;
  isGettingApplicationSuccess: boolean;
  isGettingApplicationFailed: boolean;
  applications: ApplicationSearchResponseDto;
  applicationsV2: ApplicationSearchResponseV3Dto;
  applicationsDataPages: Array<ApplicationsDataPage>;
  applicationsDataV2Pages: Array<ApplicationsDataV2Page>;
  isFetchingApplications: boolean;
  isFetchingApplicationsFailed: boolean;
  isFetchingApplicationsSuccess: boolean;
  isChangingApplicationStatus: boolean;
  isChangingApplicationStatusFailed: boolean;
  isChangingApplicationStatusSuccess: boolean;
  changeApplicationStatusResponse: ApplicationStatusChangeResponseDto | null;
  applicantResponse: ApplicationOccupantDto;
  coApplicantResources: Array<ApplicationOccupantDto>;
  isGettingApplicant: boolean;
  isGettingApplicantSuccess: boolean;
  isGettingApplicantFailed: boolean;
  createApplicantResponse: ApplicationOccupantDto;
  isCreatingApplicant: boolean;
  isCreatingApplicantSuccess: boolean;
  isCreatingApplicantFailed: boolean;
  isUpdatingApplicant: boolean;
  isUpdatingApplicantSuccess: boolean;
  isUpdatingApplicantFailed: boolean;
  isDeletingApplicant: boolean;
  isDeletingApplicantSuccess: boolean;
  isDeletingApplicantFailed: boolean;
  createPetResponse: ApplicationPetDto;
  isCreatingPet: boolean;
  isCreatingPetSuccess: boolean;
  isCreatingPetFailed: boolean;
  isUpdatingPet: boolean;
  isUpdatingPetSuccess: boolean;
  isUpdatingPetFailed: boolean;
  isDeletingPet: boolean;
  isDeletingPetSuccess: boolean;
  isDeletingPetFailed: boolean;
  createVehicleResponse: ApplicationVehicleDto;
  isCreatingVehicle: boolean;
  isCreatingVehicleSuccess: boolean;
  isCreatingVehicleFailed: boolean;
  isUpdatingVehicle: boolean;
  isUpdatingVehicleSuccess: boolean;
  isUpdatingVehicleFailed: boolean;
  isDeletingVehicle: boolean;
  isDeletingVehicleSuccess: boolean;
  isDeletingVehicleFailed: boolean;
  createEmploymentResponse: ApplicationEmploymentDto;
  isCreatingEmployment: boolean;
  isCreatingEmploymentSuccess: boolean;
  isCreatingEmploymentFailed: boolean;
  isUpdatingEmployment: boolean;
  isUpdatingEmploymentSuccess: boolean;
  isUpdatingEmploymentFailed: boolean;
  isDeletingEmployment: boolean;
  isDeletingEmploymentSuccess: boolean;
  isDeletingEmploymentFailed: boolean;
  createRentalHistoryResponse: ApplicationRentalHistoryDto;
  isCreatingRentalHistory: boolean;
  isCreatingRentalHistorySuccess: boolean;
  isCreatingRentalHistoryFailed: boolean;
  isUpdatingRentalHistory: boolean;
  isUpdatingRentalHistorySuccess: boolean;
  isUpdatingRentalHistoryFailed: boolean;
  isDeletingRentalHistory: boolean;
  isDeletingRentalHistorySuccess: boolean;
  isDeletingRentalHistoryFailed: boolean;
  applicationsError: ErrorType;
  invitedApplicationResponse: InvitedApplicationBasicDto;
  isGettingInvitedApplication: boolean;
  isGettingInvitedApplicationSuccess: boolean;
  isGettingInvitedApplicationFailed: boolean;
  invitedApplicationError: ErrorType;
  organizationTsCs: ApplicationTsCsDto;
  isFetchingContract: boolean;
  isFetchingContractFailed: boolean;
  isExportingApplicationDetails: boolean;
  isExportApplicationDetailsSuccess: boolean;
  isExportApplicationDetailsFailed: boolean;
  exportApplicationDetailError: string;
  isSendingInvitation: boolean;
  isSendingInvitationSuccess: boolean;
  isSendingInvitationFailed: boolean;
  sendingInvitationError: ErrorType;
  CoapplicantInvitationResponseDto: InviteCoapplicantResponseDto;
  isCreatingScreeningOrder: boolean;
  isCreatingScreeningOrderSuccess: boolean;
  isCreatingScreeningOrderFailed: boolean;
  createScreeningOrderResponse: ScreeningOrderDto;
  createScreeningOrderError: ErrorType;
  isGettingScreeningOrderDetails: boolean;
  isGettingScreeningOrderDetailsSuccess: boolean;
  isGettingScreeningOrderDetailsFailed: boolean;
  getScreeningOrderDetailsResponse: ScreeningOrderDto;
  getScreeningOrderDetailsError: ErrorType;
  propertyDetails: {
    propertyAddress: string;
    locationUuid: string | null | undefined;
  };
  IDontHaveSsn: boolean;
  //sending app decision notification
  isSendingApplicationDecisionNotification: boolean;
  isSendingApplicationDecisionNotificationSuccess: boolean;
  isSendingApplicationDecisionNotificationFailure: boolean;
  sendingApplicationDecisionNotificationError: ErrorType | null;
  //downloading app decision file
  isDownloadingApplicationDecisionPdf: boolean;
  isDownloadingApplicationDecisionPdfSuccess: boolean;
  isDownloadingApplicationDecisionPdfFailure: boolean;
  downloadingApplicationDecisionPdfError: ErrorType | null;
  //exporting employment release
  isExportingEmploymentRelease: boolean;
  isExportingEmploymentReleaseSuccess: boolean;
  isExportingEmploymentReleaseFailed: boolean;
  exportEmploymentReleaseError: string;
  //exporting rental release
  isExportingRentalRelease: boolean;
  isExportingRentalReleaseSuccess: boolean;
  isExportingRentalReleaseFailed: boolean;
  exportRentalReleaseError: string;
  //fetching employment release url
  isFetchingEmploymentReleaseURL: boolean;
  isFetchingEmploymentReleaseURLSuccess: boolean;
  isFetchingEmploymentReleaseURLFailed: boolean;
  fetchEmploymentReleaseURLError: ErrorType | null;
  //fetching rental history release url
  isFetchingRentalHistoryReleaseURL: boolean;
  isFetchingRentalHistoryReleaseURLSuccess: boolean;
  isFetchingRentalHistoryReleaseURLFailed: boolean;
  fetchRentalHistoryReleaseURLError: ErrorType | null;
  //fetching application pdf url
  isFetchingApplicationPdfURL: boolean;
  isFetchingApplicationPdfURLSuccess: boolean;
  isFetchingApplicationPdfURLFailed: boolean;
  fetchApplicationPdfURLError: ErrorType | null;
  //create application notes
  isCreatingApplicationNote: boolean;
  isCreatingApplicationNoteSuccess: boolean;
  isCreatingApplicationNoteFailed: boolean;
  createApplicationNoteResponse: ApplicationNoteDto;
  errorCreateApplicationNote: ErrorType | null;
  //get all application Notes
  isFetchingAllApplicationNotes: boolean;
  isFetchingAllApplicationNotesSuccess: boolean;
  isFetchingAllApplicationNotesFailed: boolean;
  allApplicationNotes: ApplicationNoteDto[];
  errorFetchingAllApplicationNotes: ErrorType | null;
  //delete an application note
  isDeletingApplicationNote: boolean;
  isDeletingApplicationNoteFailed: boolean;
  isDeletingApplicationNoteSuccess: boolean;
  errorDeletingApplicationNote: ErrorType | null;
  //fetch applicant attachment url
  isFetchingApplicantAttachmentUrl: boolean;
  isFetchingApplicantAttachmentUrlFailed: boolean;
  isFetchingApplicantAttachmentUrlSuccess: boolean;
  applicantAttachmentUrl: string;
  errorFetchingApplicantAttachmentUrl: ErrorType | null;
  //download applicant attachment
  isDownloadingApplicantAttachment: boolean;
  isDownloadingApplicantAttachmentFailed: boolean;
  isDownloadingApplicantAttachmentSuccess: boolean;
  errorDownloadingApplicantAttachment: ErrorType | null;
  //update application property
  isUpdatingAppProperty: boolean;
  isUpdatingAppPropertyFailed: boolean;
  isUpdatingAppPropertySuccess: boolean;
  errorUpdatingAppProperty: ErrorType | null;
  //exporting application data zip
  isExportingApplicationZip: boolean;
  isExportingApplicationZipSuccess: boolean;
  isExportingApplicationZipFailure: boolean;
  errorExportingApplicationZip: ErrorType | null;
};

const getInitialState = () => {
  return {
    application: {} as ApplicationV2Dto,
    isGettingApplication: false,
    isGettingApplicationFailed: false,
    isGettingApplicationSuccess: false,
    applications: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    applicationsV2: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    applicationsDataPages: [],
    applicationsDataV2Pages: [],
    isFetchingApplications: false,
    isFetchingApplicationsFailed: false,
    isFetchingApplicationsSuccess: false,
    isChangingApplicationStatus: false,
    isChangingApplicationStatusFailed: false,
    isChangingApplicationStatusSuccess: false,
    changeApplicationStatusResponse: null,
    applicantResponse: {} as ApplicationOccupantDto,
    coApplicantResources: [],
    isGettingApplicant: false,
    isGettingApplicantSuccess: false,
    isGettingApplicantFailed: false,
    createApplicantResponse: {} as ApplicationOccupantDto,
    isCreatingApplicant: false,
    isCreatingApplicantSuccess: false,
    isCreatingApplicantFailed: false,
    isCreatingScreeningOrder: false,
    isCreatingScreeningOrderSuccess: false,
    isCreatingScreeningOrderFailed: false,
    createScreeningOrderResponse: {} as ScreeningOrderDto,
    createScreeningOrderError: {} as ErrorType,
    isGettingScreeningOrderDetails: false,
    isGettingScreeningOrderDetailsSuccess: false,
    isGettingScreeningOrderDetailsFailed: false,
    getScreeningOrderDetailsResponse: {} as ScreeningOrderDto,
    getScreeningOrderDetailsError: {} as ErrorType,
    isUpdatingApplicant: false,
    isUpdatingApplicantSuccess: false,
    isUpdatingApplicantFailed: false,
    isDeletingApplicant: false,
    isDeletingApplicantSuccess: false,
    isDeletingApplicantFailed: false,
    createPetResponse: {} as ApplicationPetDto,
    isCreatingPet: false,
    isCreatingPetSuccess: false,
    isCreatingPetFailed: false,
    isUpdatingPet: false,
    isUpdatingPetSuccess: false,
    isUpdatingPetFailed: false,
    isDeletingPet: false,
    isDeletingPetSuccess: false,
    isDeletingPetFailed: false,
    createVehicleResponse: {} as ApplicationVehicleDto,
    isCreatingVehicle: false,
    isCreatingVehicleSuccess: false,
    isCreatingVehicleFailed: false,
    isUpdatingVehicle: false,
    isUpdatingVehicleSuccess: false,
    isUpdatingVehicleFailed: false,
    isDeletingVehicle: false,
    isDeletingVehicleSuccess: false,
    isDeletingVehicleFailed: false,
    createEmploymentResponse: {} as ApplicationEmploymentDto,
    isCreatingEmployment: false,
    isCreatingEmploymentSuccess: false,
    isCreatingEmploymentFailed: false,
    isUpdatingEmployment: false,
    isUpdatingEmploymentSuccess: false,
    isUpdatingEmploymentFailed: false,
    isDeletingEmployment: false,
    isDeletingEmploymentSuccess: false,
    isDeletingEmploymentFailed: false,
    createRentalHistoryResponse: {} as ApplicationRentalHistoryDto,
    isCreatingRentalHistory: false,
    isCreatingRentalHistorySuccess: false,
    isCreatingRentalHistoryFailed: false,
    isUpdatingRentalHistory: false,
    isUpdatingRentalHistorySuccess: false,
    isUpdatingRentalHistoryFailed: false,
    isDeletingRentalHistory: false,
    isDeletingRentalHistorySuccess: false,
    isDeletingRentalHistoryFailed: false,
    applicationsError: {} as ErrorType,
    invitedApplicationResponse: {} as InvitedApplicationBasicDto,
    isGettingInvitedApplication: false,
    isGettingInvitedApplicationSuccess: false,
    isGettingInvitedApplicationFailed: false,
    invitedApplicationError: {} as ErrorType,
    organizationTsCs: {
      ApplicationAgreementTsCs: {} as TermsAndConditionsDto,
      RentalHistoryConsentTsCs: {} as TermsAndConditionsDto,
      EmploymentConsentTsCs: {} as TermsAndConditionsDto,
    } as ApplicationTsCsDto,
    isFetchingContract: false,
    isFetchingContractFailed: false,
    isExportingApplicationDetails: false,
    isExportApplicationDetailsSuccess: false,
    isExportApplicationDetailsFailed: false,
    exportApplicationDetailError: '',
    isSendingInvitation: false,
    isSendingInvitationSuccess: false,
    isSendingInvitationFailed: false,
    sendingInvitationError: {} as ErrorType,
    CoapplicantInvitationResponseDto: {} as InviteCoapplicantResponseDto,
    propertyDetails: {
      propertyAddress: '',
      locationUuid: null,
    },
    IDontHaveSsn: false,
    isSendingApplicationDecisionNotification: false,
    isSendingApplicationDecisionNotificationSuccess: false,
    isSendingApplicationDecisionNotificationFailure: false,
    sendingApplicationDecisionNotificationError: null,
    isDownloadingApplicationDecisionPdf: false,
    isDownloadingApplicationDecisionPdfSuccess: false,
    isDownloadingApplicationDecisionPdfFailure: false,
    downloadingApplicationDecisionPdfError: null,
    isExportingEmploymentRelease: false,
    isExportingEmploymentReleaseSuccess: false,
    isExportingEmploymentReleaseFailed: false,
    exportEmploymentReleaseError: '',
    isExportingRentalRelease: false,
    isExportingRentalReleaseSuccess: false,
    isExportingRentalReleaseFailed: false,
    exportRentalReleaseError: '',
    isFetchingEmploymentReleaseURL: false,
    isFetchingEmploymentReleaseURLSuccess: false,
    isFetchingEmploymentReleaseURLFailed: false,
    fetchEmploymentReleaseURLError: null,
    employmentReleaseURLs: [],
    isFetchingRentalHistoryReleaseURL: false,
    isFetchingRentalHistoryReleaseURLSuccess: false,
    isFetchingRentalHistoryReleaseURLFailed: false,
    fetchRentalHistoryReleaseURLError: null,
    rentalHistoryReleaseURLs: [],
    isFetchingApplicationPdfURL: false,
    isFetchingApplicationPdfURLSuccess: false,
    isFetchingApplicationPdfURLFailed: false,
    fetchApplicationPdfURLError: null,
    isCreatingApplicationNote: false,
    isCreatingApplicationNoteSuccess: false,
    isCreatingApplicationNoteFailed: false,
    createApplicationNoteResponse: {} as ApplicationNoteDto,
    errorCreateApplicationNote: null,
    isFetchingAllApplicationNotes: false,
    isFetchingAllApplicationNotesSuccess: false,
    isFetchingAllApplicationNotesFailed: false,
    allApplicationNotes: [],
    errorFetchingAllApplicationNotes: null,
    isDeletingApplicationNote: false,
    isDeletingApplicationNoteFailed: false,
    isDeletingApplicationNoteSuccess: false,
    errorDeletingApplicationNote: null,
    isFetchingApplicantAttachmentUrl: false,
    isFetchingApplicantAttachmentUrlFailed: false,
    isFetchingApplicantAttachmentUrlSuccess: false,
    errorFetchingApplicantAttachmentUrl: null,
    applicantAttachmentUrl: '',
    isDownloadingApplicantAttachment: false,
    isDownloadingApplicantAttachmentFailed: false,
    isDownloadingApplicantAttachmentSuccess: false,
    errorDownloadingApplicantAttachment: null,
    isUpdatingAppProperty: false,
    isUpdatingAppPropertyFailed: false,
    isUpdatingAppPropertySuccess: false,
    errorUpdatingAppProperty: null,
    isExportingApplicationZip: false,
    isExportingApplicationZipSuccess: false,
    isExportingApplicationZipFailure: false,
    errorExportingApplicationZip: null,
  } as ApplicationsState;
};

const applicationsSlice = createSlice({
  name: 'applications',
  initialState: getInitialState(),
  reducers: {
    getFetchingApplicationsData(state, action) {
      state.applicationsError = {} as ErrorType;
      state.isFetchingApplicationsFailed = false;
      state.isFetchingApplications = true;
    },
    setFetchingApplicationsData(state, action) {
      const _payload = action.payload;
      let currentApplicationPages = [...state.applicationsDataPages];
      if (_payload.RecordStart === 0) currentApplicationPages = [];

      const whatPageResultsIsThis = Math.ceil(_payload.RecordStart / _payload.RangeSize);
      currentApplicationPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };
      const appsData = {
        applications: action.payload,
        applicationsDataPages: currentApplicationPages,
        isFetchingApplications: false,
        isFetchingApplicationsFailed: false,
        isFetchingApplicationsSuccess: true,
      };
      return { ...state, ...appsData };
    },
    getFetchingApplicationsDataFailed(state, action) {
      state.isFetchingApplicationsFailed = true;
      state.isFetchingApplications = false;
      state.applicationsError = action.payload;
      state.isFetchingApplicationsSuccess = false;
    },
    getFetchingApplicationsDataV2(state, action) {
      state.applicationsError = {} as ErrorType;
      state.isFetchingApplicationsFailed = false;
      state.isFetchingApplications = true;
      state.isFetchingApplicationsSuccess = false;
    },
    setFetchingApplicationsDataV2(state, action) {
      const _payload = action.payload;
      let currentApplicationV2Pages = [...state.applicationsDataV2Pages];
      if (_payload.RecordStart === 0) currentApplicationV2Pages = [];

      const whatPageResultsIsThis = Math.ceil(_payload.RecordStart / _payload.RangeSize);
      currentApplicationV2Pages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };
      const appsV2Data = {
        applicationsV2: action.payload,
        applicationsDataV2Pages: currentApplicationV2Pages,
        isFetchingApplications: false,
        isFetchingApplicationsFailed: false,
        isFetchingApplicationsSuccess: true,
      };
      return { ...state, ...appsV2Data };
    },
    getFetchingApplicationsDataV2Failed(state, action) {
      state.isFetchingApplicationsFailed = true;
      state.isFetchingApplications = false;
      state.applicationsError = action.payload;
      state.isFetchingApplicationsSuccess = false;
    },
    changeApplicationStatus(state, action) {
      state.isChangingApplicationStatus = true;
      state.isChangingApplicationStatusSuccess = false;
      state.isChangingApplicationStatusFailed = false;
    },
    handleChangeAppStatusSuccess(state, action) {
      state.isChangingApplicationStatus = false;
      state.isChangingApplicationStatusSuccess = true;
      state.isChangingApplicationStatusFailed = false;
      state.changeApplicationStatusResponse = action.payload;
    },
    handleChangeAppStatusFailed(state, action) {
      state.isChangingApplicationStatus = false;
      state.isChangingApplicationStatusSuccess = false;
      state.isChangingApplicationStatusFailed = true;
      state.applicationsError = action.payload;
    },
    getApplicant(state, action) {
      state.isGettingApplicant = true;
      state.isGettingApplicantSuccess = false;
      state.isGettingApplicantFailed = false;
    },
    handleGetApplicantSuccess(state, action) {
      state.isGettingApplicant = false;
      state.isGettingApplicantSuccess = true;
      state.isGettingApplicantFailed = false;

      if (action.payload.isGettingCoApplicant) {
        state.coApplicantResources = [...state.coApplicantResources.filter((ca) => ca.Uuid !== action.payload.data.Uuid), action.payload.data];
      } else {
        state.applicantResponse = action.payload.data;
      }
    },
    handleGetApplicantFailed(state, action) {
      state.isGettingApplicant = false;
      state.isGettingApplicantSuccess = false;
      state.isGettingApplicantFailed = true;
      state.applicationsError = action.payload;
    },
    getApplication(state, action) {
      state.isGettingApplication = true;
      state.isGettingApplicationSuccess = false;
      state.isGettingApplicationFailed = false;
    },
    getApplicationSuccess(state, action) {
      state.isGettingApplication = false;
      state.isGettingApplicationSuccess = true;
      state.isGettingApplicationFailed = false;
      state.application = action.payload;
    },
    getApplicationFailed(state, action) {
      state.isGettingApplication = false;
      state.isGettingApplicationSuccess = false;
      state.isGettingApplicationFailed = true;
      state.applicationsError = action.payload;
    },
    updateCoApplicantResources(state, action) {
      state.coApplicantResources = action.payload;
    },
    createApplicant(state, action) {
      state.createApplicantResponse = {} as ApplicationOccupantDto;
      state.isCreatingApplicant = true;
      state.isCreatingApplicantSuccess = false;
      state.isCreatingApplicantFailed = false;
    },
    handleCreateApplicantSuccess(state, action) {
      state.createApplicantResponse = action.payload;
      state.isCreatingApplicant = false;
      state.isCreatingApplicantSuccess = true;
      state.isCreatingApplicantFailed = false;
    },
    handleCreateApplicantFailed(state, action) {
      state.isCreatingApplicant = false;
      state.isCreatingApplicantSuccess = false;
      state.isCreatingApplicantFailed = true;
      state.applicationsError = action.payload;
    },
    createScreeningOrder(state, action) {
      state.createScreeningOrderResponse = {} as ScreeningOrderDto;
      state.isCreatingScreeningOrder = true;
      state.isCreatingScreeningOrderSuccess = false;
      state.isCreatingScreeningOrderFailed = false;
    },
    handleCreateScreeningOrderSuccess(state, action) {
      state.createScreeningOrderResponse = action.payload;
      state.isCreatingScreeningOrder = false;
      state.isCreatingScreeningOrderSuccess = true;
      state.isCreatingScreeningOrderFailed = false;
      if (state.application.ScreeningOrders?.length) state.application.ScreeningOrders = [...state.application.ScreeningOrders, action.payload];
      else state.application.ScreeningOrders = [action.payload];
    },
    handleCreateScreeningOrderFailed(state, action) {
      state.isCreatingScreeningOrder = false;
      state.isCreatingScreeningOrderSuccess = false;
      state.isCreatingScreeningOrderFailed = true;
      state.createScreeningOrderError = action.payload;
    },
    getScreeningOrderDetails(state) {
      state.isGettingScreeningOrderDetails = true;
      state.isGettingScreeningOrderDetailsSuccess = false;
      state.isGettingScreeningOrderDetailsFailed = false;
      state.getScreeningOrderDetailsResponse = {} as ScreeningOrderDto;
    },
    handleGetScreeningOrderDetailsSuccess(state, action) {
      state.isGettingScreeningOrderDetails = false;
      state.isGettingScreeningOrderDetailsSuccess = true;
      state.isGettingScreeningOrderDetailsFailed = false;
      state.getScreeningOrderDetailsError = {} as ErrorType;
      state.getScreeningOrderDetailsResponse = action.payload;
    },
    handleGetScreeningOrderDetailsFailed(state, action) {
      state.isGettingScreeningOrderDetails = false;
      state.isGettingScreeningOrderDetailsSuccess = false;
      state.isGettingScreeningOrderDetailsFailed = true;
      state.getScreeningOrderDetailsError = action.payload;
    },
    updateApplicant(state, action) {
      state.isUpdatingApplicant = true;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = false;
    },
    handleUpdateApplicantSuccess(state, action) {
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = true;
      state.isUpdatingApplicantFailed = false;
    },
    handleUpdateApplicantFailed(state, action) {
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = true;
      state.applicationsError = action.payload;
    },
    deleteApplicant(state, action) {
      state.isDeletingApplicant = true;
      state.isDeletingApplicantSuccess = false;
      state.isDeletingApplicantFailed = false;
    },
    handleDeleteApplicantSuccess(state, action) {
      state.isDeletingApplicant = false;
      state.isDeletingApplicantSuccess = true;
      state.isDeletingApplicantFailed = false;
    },
    handleDeleteApplicantFailed(state, action) {
      state.isDeletingApplicant = false;
      state.isDeletingApplicantSuccess = false;
      state.isDeletingApplicantFailed = true;
      state.applicationsError = action.payload;
    },
    createPet(state, action) {
      state.createPetResponse = {} as ApplicationPetDto;
      state.isCreatingPet = true;
      state.isCreatingPetSuccess = false;
      state.isCreatingPetFailed = false;
    },
    handleCreatePetSuccess(state, action) {
      state.createPetResponse = action.payload;
      state.isCreatingPet = false;
      state.isCreatingPetSuccess = true;
      state.isCreatingPetFailed = false;
    },
    handleCreatePetFailed(state, action) {
      state.isCreatingPet = false;
      state.isCreatingPetSuccess = false;
      state.isCreatingPetFailed = true;
      state.applicationsError = action.payload;
    },
    updatePet(state, action) {
      state.isUpdatingPet = true;
      state.isUpdatingPetSuccess = false;
      state.isUpdatingPetFailed = false;
    },
    handleUpdatePetSuccess(state, action) {
      state.isUpdatingPet = false;
      state.isUpdatingPetSuccess = true;
      state.isUpdatingPetFailed = false;
    },
    handleUpdatePetFailed(state, action) {
      state.isUpdatingPet = false;
      state.isUpdatingPetSuccess = false;
      state.isUpdatingPetFailed = true;
      state.applicationsError = action.payload;
    },
    deletePet(state, action) {
      state.isDeletingPet = true;
      state.isDeletingPetSuccess = false;
      state.isDeletingPetFailed = false;
    },
    handleDeletePetSuccess(state, action) {
      state.isDeletingPet = false;
      state.isDeletingPetSuccess = true;
      state.isDeletingPetFailed = false;
    },
    handleDeletePetFailed(state, action) {
      state.isDeletingPet = false;
      state.isDeletingPetSuccess = false;
      state.isDeletingPetFailed = true;
      state.applicationsError = action.payload;
    },
    createVehicle(state, action) {
      state.createVehicleResponse = {} as ApplicationVehicleDto;
      state.isCreatingVehicle = true;
      state.isCreatingVehicleSuccess = false;
      state.isCreatingVehicleFailed = false;
    },
    handleCreateVehicleSuccess(state, action) {
      state.createVehicleResponse = action.payload;
      state.isCreatingVehicle = false;
      state.isCreatingVehicleSuccess = true;
      state.isCreatingVehicleFailed = false;
    },
    handleCreateVehicleFailed(state, action) {
      state.isCreatingVehicle = false;
      state.isCreatingVehicleSuccess = false;
      state.isCreatingVehicleFailed = true;
      state.applicationsError = action.payload;
    },
    updateVehicle(state, action) {
      state.isUpdatingVehicle = true;
      state.isUpdatingVehicleSuccess = false;
      state.isUpdatingVehicleFailed = false;
    },
    handleUpdateVehicleSuccess(state, action) {
      state.isUpdatingVehicle = false;
      state.isUpdatingVehicleSuccess = true;
      state.isUpdatingVehicleFailed = false;
    },
    handleUpdateVehicleFailed(state, action) {
      state.isUpdatingVehicle = false;
      state.isUpdatingVehicleSuccess = false;
      state.isUpdatingVehicleFailed = true;
      state.applicationsError = action.payload;
    },
    deleteVehicle(state, action) {
      state.isDeletingVehicle = true;
      state.isDeletingVehicleSuccess = false;
      state.isDeletingVehicleFailed = false;
    },
    handleDeleteVehicleSuccess(state, action) {
      state.isDeletingVehicle = false;
      state.isDeletingVehicleSuccess = true;
      state.isDeletingVehicleFailed = false;
    },
    handleDeleteVehicleFailed(state, action) {
      state.isDeletingVehicle = false;
      state.isDeletingVehicleSuccess = false;
      state.isDeletingVehicleFailed = true;
      state.applicationsError = action.payload;
    },
    createEmployment(state, action) {
      state.createEmploymentResponse = {} as ApplicationEmploymentDto;
      state.isCreatingEmployment = true;
      state.isCreatingEmploymentSuccess = false;
      state.isCreatingEmploymentFailed = false;
    },
    handleCreateEmploymentSuccess(state, action) {
      state.createEmploymentResponse = action.payload;
      state.isCreatingEmployment = false;
      state.isCreatingEmploymentSuccess = true;
      state.isCreatingEmploymentFailed = false;
    },
    handleCreateEmploymentFailed(state, action) {
      state.isCreatingEmployment = false;
      state.isCreatingEmploymentSuccess = false;
      state.isCreatingEmploymentFailed = true;
      state.applicationsError = action.payload;
    },
    updateEmployment(state, action) {
      state.isUpdatingEmployment = true;
      state.isUpdatingEmploymentSuccess = false;
      state.isUpdatingEmploymentFailed = false;
    },
    handleUpdateEmploymentSuccess(state, action) {
      state.isUpdatingEmployment = false;
      state.isUpdatingEmploymentSuccess = true;
      state.isUpdatingEmploymentFailed = false;
    },
    handleUpdateEmploymentFailed(state, action) {
      state.isUpdatingEmployment = false;
      state.isUpdatingEmploymentSuccess = false;
      state.isUpdatingEmploymentFailed = true;
      state.applicationsError = action.payload;
    },
    deleteEmployment(state, action) {
      state.isDeletingEmployment = true;
      state.isDeletingEmploymentSuccess = false;
      state.isDeletingEmploymentFailed = false;
    },
    handleDeleteEmploymentSuccess(state, action) {
      state.isDeletingEmployment = false;
      state.isDeletingEmploymentSuccess = true;
      state.isDeletingEmploymentFailed = false;
    },
    handleDeleteEmploymentFailed(state, action) {
      state.isDeletingEmployment = false;
      state.isDeletingEmploymentSuccess = false;
      state.isDeletingEmploymentFailed = true;
      state.applicationsError = action.payload;
    },
    createRentalHistory(state, action) {
      state.createRentalHistoryResponse = {} as ApplicationRentalHistoryDto;
      state.isCreatingRentalHistory = true;
      state.isCreatingRentalHistorySuccess = false;
      state.isCreatingRentalHistoryFailed = false;
    },
    handleCreateRentalHistorySuccess(state, action) {
      state.createRentalHistoryResponse = action.payload;
      state.isCreatingRentalHistory = false;
      state.isCreatingRentalHistorySuccess = true;
      state.isCreatingRentalHistoryFailed = false;
    },
    handleCreateRentalHistoryFailed(state, action) {
      state.isCreatingRentalHistory = false;
      state.isCreatingRentalHistorySuccess = false;
      state.isCreatingRentalHistoryFailed = true;
      state.applicationsError = action.payload;
    },
    updateRentalHistory(state, action) {
      state.isUpdatingRentalHistory = true;
      state.isUpdatingRentalHistorySuccess = false;
      state.isUpdatingRentalHistoryFailed = false;
    },
    handleUpdateRentalHistorySuccess(state, action) {
      state.isUpdatingRentalHistory = false;
      state.isUpdatingRentalHistorySuccess = true;
      state.isUpdatingRentalHistoryFailed = false;
    },
    handleUpdateRentalHistoryFailed(state, action) {
      state.isUpdatingRentalHistory = false;
      state.isUpdatingRentalHistorySuccess = false;
      state.isUpdatingRentalHistoryFailed = true;
      state.applicationsError = action.payload;
    },
    deleteRentalHistory(state, action) {
      state.isDeletingRentalHistory = true;
      state.isDeletingRentalHistorySuccess = false;
      state.isDeletingRentalHistoryFailed = false;
    },
    handleDeleteRentalHistorySuccess(state, action) {
      state.isDeletingRentalHistory = false;
      state.isDeletingRentalHistorySuccess = true;
      state.isDeletingRentalHistoryFailed = false;
    },
    handleDeleteRentalHistoryFailed(state, action) {
      state.isDeletingRentalHistory = false;
      state.isDeletingRentalHistorySuccess = false;
      state.isDeletingRentalHistoryFailed = true;
      state.applicationsError = action.payload;
    },
    getInvitedApplication(state, action) {
      state.isGettingInvitedApplication = true;
      state.isGettingInvitedApplicationSuccess = false;
      state.isGettingInvitedApplicationFailed = false;
    },
    handleGetInvitedApplicationSuccess(state, action) {
      state.isGettingInvitedApplication = false;
      state.isGettingInvitedApplicationSuccess = true;
      state.isGettingInvitedApplicationFailed = false;
      state.invitedApplicationResponse = action.payload;
    },
    handleGetInvitedApplicationFailed(state, action) {
      state.isGettingInvitedApplication = false;
      state.isGettingInvitedApplicationSuccess = false;
      state.isGettingInvitedApplicationFailed = true;
      state.invitedApplicationError = action.payload;
    },
    getCompanyContract(state, action) {
      state.isFetchingContract = true;
      state.isFetchingContractFailed = false;
    },
    handleGetOrganizationContractSuccess(state, action) {
      state.isFetchingContract = false;
      state.organizationTsCs = action.payload;
    },
    handleGetContractFailed(state, action) {
      state.isFetchingContract = false;
      state.isFetchingContractFailed = true;
    },
    getExportApplicationDetails(state) {
      state.isExportingApplicationDetails = true;
      state.isExportApplicationDetailsSuccess = false;
      state.isExportApplicationDetailsFailed = false;
      state.exportApplicationDetailError = '';
    },
    setExportApplicationDetails(state) {
      state.isExportingApplicationDetails = false;
      state.isExportApplicationDetailsSuccess = true;
      state.isExportApplicationDetailsFailed = false;
      state.exportApplicationDetailError = '';
    },
    setExportApplicationDetailsError(state, action) {
      state.isExportingApplicationDetails = false;
      state.isExportApplicationDetailsSuccess = false;
      state.isExportApplicationDetailsFailed = true;
      state.exportApplicationDetailError = action.payload.message;
    },
    getExportApplicationPdf(state) {
      state.isFetchingApplicationPdfURL = true;
      state.isFetchingApplicationPdfURLSuccess = false;
      state.isFetchingApplicationPdfURLFailed = false;
      state.fetchApplicationPdfURLError = null;
    },
    setApplicationPdfUrl(state) {
      state.isFetchingApplicationPdfURL = false;
      state.isFetchingApplicationPdfURLSuccess = true;
      state.isFetchingApplicationPdfURLFailed = false;
      state.fetchApplicationPdfURLError = null;
    },
    setApplicationPdfUrlError(state, action) {
      state.isFetchingApplicationPdfURL = false;
      state.isFetchingApplicationPdfURLSuccess = false;
      state.isFetchingApplicationPdfURLFailed = true;
      state.fetchApplicationPdfURLError = action.payload;
    },
    sendCoapplicantInvitation(state) {
      state.isSendingInvitation = true;
      state.isSendingInvitationSuccess = false;
      state.isSendingInvitationFailed = false;
      state.sendingInvitationError = {} as ErrorType;
    },
    handleSendingInvitationSuccess(state, action) {
      state.isSendingInvitation = false;
      state.isSendingInvitationSuccess = true;
      state.isSendingInvitationFailed = false;
      state.CoapplicantInvitationResponseDto = action.payload;
    },
    handleSendingInvitationFailed(state, action) {
      state.isSendingInvitation = false;
      state.isSendingInvitationSuccess = false;
      state.isSendingInvitationFailed = true;
      state.sendingInvitationError = action.payload;
    },
    setApplicantPropertyDetails(state, action) {
      state.propertyDetails = action.payload;
    },
    resetApplicationDashboardState(state) {
      return {
        ...getInitialState(),
        applications: state.applications,
        applicationsDataPages: state.applicationsDataPages,
        applicantResponse: state.applicantResponse,
        organizationTsCs: state.organizationTsCs,
        IDontHaveSsn: state.IDontHaveSsn,
      };
    },
    resetApplicationStatusChangeStates(state) {
      state.isChangingApplicationStatus = false;
      state.isChangingApplicationStatusFailed = false;
      state.isChangingApplicationStatusSuccess = false;
    },
    resetApplicantState(state) {
      state.isCreatingApplicant = false;
      state.isCreatingApplicantSuccess = false;
      state.isCreatingApplicantFailed = false;
      state.isUpdatingApplicant = false;
      state.isUpdatingApplicantSuccess = false;
      state.isUpdatingApplicantFailed = false;
      state.isDeletingApplicant = false;
      state.isDeletingApplicantSuccess = false;
      state.isDeletingApplicantFailed = false;
    },
    resetPetState(state) {
      state.isCreatingPet = false;
      state.isCreatingPetSuccess = false;
      state.isCreatingPetFailed = false;
      state.isUpdatingPet = false;
      state.isUpdatingPetSuccess = false;
      state.isUpdatingPetFailed = false;
      state.isDeletingPet = false;
      state.isDeletingPetSuccess = false;
      state.isDeletingPetFailed = false;
    },
    resetVehicleState(state) {
      state.isCreatingVehicle = false;
      state.isCreatingVehicleSuccess = false;
      state.isCreatingVehicleFailed = false;
      state.isUpdatingVehicle = false;
      state.isUpdatingVehicleSuccess = false;
      state.isUpdatingVehicleFailed = false;
      state.isDeletingVehicle = false;
      state.isDeletingVehicleSuccess = false;
      state.isDeletingVehicleFailed = false;
    },
    resetEmploymentState(state) {
      state.isCreatingEmployment = false;
      state.isCreatingEmploymentSuccess = false;
      state.isCreatingEmploymentFailed = false;
      state.isUpdatingEmployment = false;
      state.isUpdatingEmploymentSuccess = false;
      state.isUpdatingEmploymentFailed = false;
      state.isDeletingEmployment = false;
      state.isDeletingEmploymentSuccess = false;
      state.isDeletingEmploymentFailed = false;
    },
    resetRentalHistoryState(state) {
      state.isCreatingRentalHistory = false;
      state.isCreatingRentalHistorySuccess = false;
      state.isCreatingRentalHistoryFailed = false;
      state.isUpdatingRentalHistory = false;
      state.isUpdatingRentalHistorySuccess = false;
      state.isUpdatingRentalHistoryFailed = false;
      state.isDeletingRentalHistory = false;
      state.isDeletingRentalHistorySuccess = false;
      state.isDeletingRentalHistoryFailed = false;
    },
    resetScreeningOrderState(state) {
      state.createScreeningOrderError = {} as ErrorType;
      state.createScreeningOrderResponse = {} as ScreeningOrderDto;
      state.isCreatingScreeningOrder = false;
      state.isCreatingScreeningOrderFailed = false;
      state.isCreatingScreeningOrderSuccess = false;
    },
    resetGetScreeningOrderDetailsState(state) {
      state.isGettingScreeningOrderDetails = false;
      state.isGettingScreeningOrderDetailsSuccess = false;
      state.isGettingScreeningOrderDetailsFailed = false;
      state.getScreeningOrderDetailsResponse = {} as ScreeningOrderDto;
      state.getScreeningOrderDetailsError = {} as ErrorType;
    },
    setIDontHaveSsn(state, action) {
      state.IDontHaveSsn = action.payload;
    },
    sendApplicationDecisionNotification(state) {
      state.isSendingApplicationDecisionNotification = true;
      state.isSendingApplicationDecisionNotificationFailure = false;
      state.isSendingApplicationDecisionNotificationSuccess = false;
      state.sendingApplicationDecisionNotificationError = null;
    },
    handleSendApplicationDecisionNotificationSuccess(state) {
      state.isSendingApplicationDecisionNotification = false;
      state.isSendingApplicationDecisionNotificationFailure = false;
      state.isSendingApplicationDecisionNotificationSuccess = true;
    },
    handleSendApplicationDecisionNotificationFailed(state, action) {
      state.isSendingApplicationDecisionNotification = false;
      state.isSendingApplicationDecisionNotificationFailure = true;
      state.isSendingApplicationDecisionNotificationSuccess = false;
      state.sendingApplicationDecisionNotificationError = action.payload;
    },
    downloadApplicationDecisionPdf(state) {
      state.isDownloadingApplicationDecisionPdf = true;
      state.isDownloadingApplicationDecisionPdfSuccess = false;
      state.isDownloadingApplicationDecisionPdfFailure = false;
      state.downloadingApplicationDecisionPdfError = null;
    },
    handleDownloadApplicationDecisionPdfSuccess(state) {
      state.isDownloadingApplicationDecisionPdf = false;
      state.isDownloadingApplicationDecisionPdfSuccess = true;
      state.isDownloadingApplicationDecisionPdfFailure = false;
    },
    handleDownloadingApplicationDecisionPdfFailure(state, action) {
      state.isDownloadingApplicationDecisionPdf = false;
      state.isDownloadingApplicationDecisionPdfSuccess = false;
      state.isDownloadingApplicationDecisionPdfFailure = true;
      state.downloadingApplicationDecisionPdfError = action.payload;
    },
    exportEmploymentRelease(state) {
      state.isExportingEmploymentRelease = true;
      state.isExportingEmploymentReleaseSuccess = false;
      state.isExportingEmploymentReleaseFailed = false;
      state.exportEmploymentReleaseError = '';
    },
    setExportEmploymentRelease(state) {
      state.isExportingEmploymentRelease = false;
      state.isExportingEmploymentReleaseSuccess = true;
      state.isExportingEmploymentReleaseFailed = false;
      state.exportEmploymentReleaseError = '';
    },
    setExportEmploymentReleaseError(state, action) {
      state.isExportingEmploymentRelease = false;
      state.isExportingEmploymentReleaseSuccess = false;
      state.isExportingEmploymentReleaseFailed = true;
      state.exportEmploymentReleaseError = action.payload.message;
    },
    exportRentalRelease(state) {
      state.isExportingRentalRelease = true;
      state.isExportingRentalReleaseSuccess = false;
      state.isExportingRentalReleaseFailed = false;
      state.exportRentalReleaseError = '';
    },
    setExportRentalRelease(state) {
      state.isExportingRentalRelease = false;
      state.isExportingRentalReleaseSuccess = true;
      state.isExportingRentalReleaseFailed = false;
      state.exportRentalReleaseError = '';
    },
    setExportRentalReleaseError(state, action) {
      state.isExportingRentalRelease = false;
      state.isExportingRentalReleaseSuccess = false;
      state.isExportingRentalReleaseFailed = true;
      state.exportRentalReleaseError = action.payload.message;
    },
    fetchEmploymentReleaseURL(state) {
      state.isFetchingEmploymentReleaseURL = true;
      state.isFetchingEmploymentReleaseURLFailed = false;
      state.isFetchingEmploymentReleaseURLSuccess = false;
      state.fetchEmploymentReleaseURLError = null;
    },
    setEmploymentReleaseURL(state) {
      state.isFetchingEmploymentReleaseURL = false;
      state.isFetchingEmploymentReleaseURLFailed = false;
      state.isFetchingEmploymentReleaseURLSuccess = true;
      state.fetchEmploymentReleaseURLError = null;
    },
    setEmploymentReleaseURLError(state, action) {
      state.isFetchingEmploymentReleaseURL = false;
      state.isFetchingEmploymentReleaseURLFailed = true;
      state.isFetchingEmploymentReleaseURLSuccess = false;
      state.fetchEmploymentReleaseURLError = action.payload;
    },
    fetchRentalHistoryReleaseURL(state) {
      state.isFetchingRentalHistoryReleaseURL = true;
      state.isFetchingRentalHistoryReleaseURLFailed = false;
      state.isFetchingRentalHistoryReleaseURLSuccess = false;
      state.fetchRentalHistoryReleaseURLError = null;
    },
    setRentalHistoryReleaseURL(state) {
      state.isFetchingRentalHistoryReleaseURL = false;
      state.isFetchingRentalHistoryReleaseURLFailed = false;
      state.isFetchingRentalHistoryReleaseURLSuccess = true;
      state.fetchRentalHistoryReleaseURLError = null;
    },
    setRentalHistoryURLReleaseURLError(state, action) {
      state.isFetchingRentalHistoryReleaseURL = false;
      state.isFetchingRentalHistoryReleaseURLFailed = true;
      state.isFetchingRentalHistoryReleaseURLSuccess = false;
      state.fetchRentalHistoryReleaseURLError = action.payload;
    },
    /**At first glance the two functions below might look like they are doing the same thing, but there is a subtle difference */
    handleGoBackAppDetailsSummary(state) {
      //reset application url states
      state.isFetchingApplicationPdfURLSuccess = false;
      state.isFetchingApplicationPdfURLFailed = false;
      //reset employment release url states
      state.isFetchingEmploymentReleaseURLSuccess = false;
      state.isFetchingEmploymentReleaseURLFailed = false;
      //reset rental release url states
      state.isFetchingRentalHistoryReleaseURLSuccess = false;
      state.isFetchingRentalHistoryReleaseURLFailed = false;
      //reset application export states
      state.isExportApplicationDetailsSuccess = false;
      state.isExportApplicationDetailsFailed = false;
      //reset employment release export states
      state.isExportingEmploymentReleaseSuccess = false;
      state.isExportingEmploymentReleaseFailed = false;
      //reset rental release export states
      state.isExportingRentalReleaseSuccess = false;
      state.isExportingRentalReleaseFailed = false;
      //reset app details call state
      state.isGettingApplicationSuccess = false;
      state.application = {} as ApplicationV2Dto;
      //reset notes states
      state.allApplicationNotes = [];
      state.isCreatingApplicationNoteSuccess = false;
      state.isDeletingApplicationNoteSuccess = false;
      //reset attachment url states
      state.isFetchingApplicantAttachmentUrlSuccess = false;
      state.isFetchingApplicantAttachmentUrlFailed = false;
      state.applicantAttachmentUrl = '';
      //reset attachment download states
      state.isDownloadingApplicantAttachmentFailed = false;
      state.isDownloadingApplicantAttachmentSuccess = false;
      //reset app status change states
      state.isChangingApplicationStatusSuccess = false;
      state.isChangingApplicationStatusFailed = false;
      state.changeApplicationStatusResponse = null;
      //reset app decision notification states
      state.isSendingApplicationDecisionNotificationSuccess = false;
      state.isSendingApplicationDecisionNotificationFailure = false;
    },
    handleTabChangeAppDetailsSummary(state) {
      //reset application url states
      state.isFetchingApplicationPdfURLSuccess = false;
      state.isFetchingApplicationPdfURLFailed = false;
      //reset employment release url states
      state.isFetchingEmploymentReleaseURLSuccess = false;
      state.isFetchingEmploymentReleaseURLFailed = false;
      //reset rental release url states
      state.isFetchingRentalHistoryReleaseURLSuccess = false;
      state.isFetchingRentalHistoryReleaseURLFailed = false;
      //reset application export states
      state.isExportApplicationDetailsSuccess = false;
      state.isExportApplicationDetailsFailed = false;
      //reset employment release export states
      state.isExportingEmploymentReleaseSuccess = false;
      state.isExportingEmploymentReleaseFailed = false;
      //reset rental release export states
      state.isExportingRentalReleaseSuccess = false;
      state.isExportingRentalReleaseFailed = false;
      //reset notes creation/deletion states
      state.isCreatingApplicationNoteSuccess = false;
      state.isCreatingApplicationNoteFailed = false;
      state.isDeletingApplicationNoteSuccess = false;
      state.isDeletingApplicationNoteFailed = false;
      //reset attachment url states
      state.isFetchingApplicantAttachmentUrlSuccess = false;
      state.isFetchingApplicantAttachmentUrlFailed = false;
      state.applicantAttachmentUrl = '';
      //reset attachment download states
      state.isDownloadingApplicantAttachmentFailed = false;
      state.isDownloadingApplicantAttachmentSuccess = false;
      //reset app decision notification states
      state.isSendingApplicationDecisionNotificationSuccess = false;
      state.isSendingApplicationDecisionNotificationFailure = false;
    },

    createApplicationNote(state) {
      state.isCreatingApplicationNote = true;
      state.isCreatingApplicationNoteFailed = false;
      state.isCreatingApplicationNoteSuccess = false;
      state.errorCreateApplicationNote = null;
    },
    setApplicationNote(state, action) {
      state.isCreatingApplicationNote = false;
      state.isCreatingApplicationNoteFailed = false;
      state.isCreatingApplicationNoteSuccess = true;
      state.createApplicationNoteResponse = action.payload;
    },
    setErrorCreateApplicationNote(state, action) {
      state.isCreatingApplicationNote = false;
      state.isCreatingApplicationNoteFailed = true;
      state.isCreatingApplicationNoteSuccess = false;
      state.errorCreateApplicationNote = action.payload;
    },
    clearApplicationNotesStates(state) {
      state.isCreatingApplicationNoteSuccess = false;
      state.isCreatingApplicationNoteFailed = false;
      state.errorCreateApplicationNote = null;
    },
    getAllApplicationNotes(state) {
      state.isFetchingAllApplicationNotes = true;
      state.isFetchingAllApplicationNotesFailed = false;
      state.isFetchingAllApplicationNotesSuccess = false;
      state.errorFetchingAllApplicationNotes = null;
    },
    setAllApplicationNotes(state, action) {
      state.isFetchingAllApplicationNotes = false;
      state.isFetchingAllApplicationNotesFailed = false;
      state.isFetchingAllApplicationNotesSuccess = true;
      state.allApplicationNotes = action.payload;
    },
    setErrorFetchAllApplicationNotes(state, action) {
      state.isFetchingAllApplicationNotes = false;
      state.isFetchingAllApplicationNotesFailed = true;
      state.isFetchingAllApplicationNotesSuccess = false;
      state.errorFetchingAllApplicationNotes = action.payload;
    },
    deleteApplicationNote(state) {
      state.isDeletingApplicationNote = true;
      state.isDeletingApplicationNoteFailed = false;
      state.isDeletingApplicationNoteSuccess = false;
      state.errorDeletingApplicationNote = null;
    },
    setDeleteApplicationNote(state) {
      state.isDeletingApplicationNote = false;
      state.isDeletingApplicationNoteFailed = false;
      state.isDeletingApplicationNoteSuccess = true;
    },
    setErrorDeleteApplicationNote(state, action) {
      state.isDeletingApplicationNote = false;
      state.isDeletingApplicationNoteFailed = true;
      state.isDeletingApplicationNoteSuccess = false;
      state.errorDeletingApplicationNote = action.payload;
    },
    fetchApplicantAttachmentUrl(state, action) {
      state.isFetchingApplicantAttachmentUrl = true;
      state.isFetchingApplicantAttachmentUrlFailed = false;
      state.isFetchingApplicantAttachmentUrlSuccess = false;
      state.errorFetchingApplicantAttachmentUrl = null;
      state.applicantAttachmentUrl = '';
    },
    setApplicantAttachmentUrl(state, action) {
      state.isFetchingApplicantAttachmentUrl = false;
      state.isFetchingApplicantAttachmentUrlFailed = false;
      state.isFetchingApplicantAttachmentUrlSuccess = true;
      state.errorFetchingApplicantAttachmentUrl = null;
      state.applicantAttachmentUrl = action.payload;
    },
    setErrorApplicantAttachmentUrl(state, action) {
      state.isFetchingApplicantAttachmentUrl = false;
      state.isFetchingApplicantAttachmentUrlFailed = true;
      state.isFetchingApplicantAttachmentUrlSuccess = false;
      state.errorFetchingApplicantAttachmentUrl = action.payload;
    },
    downloadApplicantAttachment(state) {
      state.isDownloadingApplicantAttachment = true;
      state.isDownloadingApplicantAttachmentFailed = false;
      state.isDownloadingApplicantAttachmentSuccess = false;
      state.errorDownloadingApplicantAttachment = null;
    },
    setDownloadApplicantAttachment(state) {
      state.isDownloadingApplicantAttachment = false;
      state.isDownloadingApplicantAttachmentFailed = false;
      state.isDownloadingApplicantAttachmentSuccess = true;
    },
    setErrorDownloadApplicantAttachment(state, action) {
      state.isDownloadingApplicantAttachment = false;
      state.isDownloadingApplicantAttachmentFailed = true;
      state.isDownloadingApplicantAttachmentSuccess = false;
      state.errorDownloadingApplicantAttachment = action.payload;
    },
    updateApplicationProperty(state) {
      state.isUpdatingAppProperty = true;
      state.isUpdatingAppPropertyFailed = false;
      state.isUpdatingAppPropertySuccess = false;
    },
    setUpdateApplicationProperty(state) {
      state.isUpdatingAppProperty = false;
      state.isUpdatingAppPropertyFailed = false;
      state.isUpdatingAppPropertySuccess = true;
    },
    setErrorUpdateApplicationProperty(state, action) {
      state.isUpdatingAppProperty = false;
      state.isUpdatingAppPropertyFailed = true;
      state.isUpdatingAppPropertySuccess = false;
      state.errorUpdatingAppProperty = action.payload;
    },
    resetUpdateApplicationPropertyStates(state) {
      state.isUpdatingAppProperty = false;
      state.isUpdatingAppPropertyFailed = false;
      state.isUpdatingAppPropertySuccess = false;
      state.errorUpdatingAppProperty = null;
    },
    exportApplicationZip(state) {
      state.isExportingApplicationZip = true;
      state.isExportingApplicationZipSuccess = false;
      state.isExportingApplicationZipFailure = false;
      state.errorExportingApplicationZip = null;
    },
    setExportApplicationZip(state) {
      state.isExportingApplicationZip = false;
      state.isExportingApplicationZipSuccess = true;
      state.isExportingApplicationZipFailure = false;
      state.errorExportingApplicationZip = null;
    },
    setErrorExportApplicationZip(state, action) {
      state.isExportingApplicationZip = false;
      state.isExportingApplicationZipSuccess = false;
      state.isExportingApplicationZipFailure = true;
      state.errorExportingApplicationZip = action.payload;
    },
    clearExportApplicationDetailStates(state) {
      state.isExportApplicationDetailsSuccess = false;
      state.isExportApplicationDetailsFailed = false;
    },
  },
});
export const {
  getFetchingApplicationsData,
  setFetchingApplicationsData,
  getFetchingApplicationsDataFailed,
  getFetchingApplicationsDataV2,
  setFetchingApplicationsDataV2,
  getFetchingApplicationsDataV2Failed,
  getApplicant,
  handleGetApplicantSuccess,
  handleGetApplicantFailed,
  getApplication,
  getApplicationFailed,
  getApplicationSuccess,
  updateCoApplicantResources,
  handleChangeAppStatusFailed,
  handleChangeAppStatusSuccess,
  changeApplicationStatus,
  createApplicant,
  handleCreateApplicantSuccess,
  handleCreateApplicantFailed,
  createScreeningOrder,
  handleCreateScreeningOrderSuccess,
  handleCreateScreeningOrderFailed,
  getScreeningOrderDetails,
  handleGetScreeningOrderDetailsSuccess,
  handleGetScreeningOrderDetailsFailed,
  updateApplicant,
  handleUpdateApplicantSuccess,
  handleUpdateApplicantFailed,
  deleteApplicant,
  handleDeleteApplicantSuccess,
  handleDeleteApplicantFailed,
  createPet,
  handleCreatePetSuccess,
  handleCreatePetFailed,
  updatePet,
  handleUpdatePetSuccess,
  handleUpdatePetFailed,
  deletePet,
  handleDeletePetSuccess,
  handleDeletePetFailed,
  createVehicle,
  handleCreateVehicleSuccess,
  handleCreateVehicleFailed,
  updateVehicle,
  handleUpdateVehicleSuccess,
  handleUpdateVehicleFailed,
  deleteVehicle,
  handleDeleteVehicleSuccess,
  handleDeleteVehicleFailed,
  createEmployment,
  handleCreateEmploymentSuccess,
  handleCreateEmploymentFailed,
  updateEmployment,
  handleUpdateEmploymentSuccess,
  handleUpdateEmploymentFailed,
  deleteEmployment,
  handleDeleteEmploymentSuccess,
  handleDeleteEmploymentFailed,
  createRentalHistory,
  handleCreateRentalHistorySuccess,
  handleCreateRentalHistoryFailed,
  updateRentalHistory,
  handleUpdateRentalHistorySuccess,
  handleUpdateRentalHistoryFailed,
  deleteRentalHistory,
  handleDeleteRentalHistorySuccess,
  handleDeleteRentalHistoryFailed,
  getInvitedApplication,
  handleGetInvitedApplicationSuccess,
  handleGetInvitedApplicationFailed,
  getCompanyContract,
  handleGetOrganizationContractSuccess,
  handleGetContractFailed,
  getExportApplicationDetails,
  setExportApplicationDetails,
  setExportApplicationDetailsError,
  getExportApplicationPdf,
  setApplicationPdfUrl,
  sendCoapplicantInvitation,
  handleSendingInvitationSuccess,
  handleSendingInvitationFailed,
  setApplicantPropertyDetails,
  resetApplicationDashboardState,
  resetApplicationStatusChangeStates,
  resetApplicantState,
  resetPetState,
  resetVehicleState,
  resetEmploymentState,
  resetRentalHistoryState,
  resetScreeningOrderState,
  setIDontHaveSsn,
  sendApplicationDecisionNotification,
  handleSendApplicationDecisionNotificationFailed,
  handleSendApplicationDecisionNotificationSuccess,
  downloadApplicationDecisionPdf,
  handleDownloadApplicationDecisionPdfSuccess,
  handleDownloadingApplicationDecisionPdfFailure,
  exportEmploymentRelease,
  exportRentalRelease,
  setExportEmploymentRelease,
  setExportEmploymentReleaseError,
  setExportRentalRelease,
  setExportRentalReleaseError,
  fetchEmploymentReleaseURL,
  fetchRentalHistoryReleaseURL,
  setEmploymentReleaseURL,
  setEmploymentReleaseURLError,
  setRentalHistoryReleaseURL,
  setRentalHistoryURLReleaseURLError,
  setApplicationPdfUrlError,
  createApplicationNote,
  setApplicationNote,
  setErrorCreateApplicationNote,
  clearApplicationNotesStates,
  getAllApplicationNotes,
  setAllApplicationNotes,
  setErrorFetchAllApplicationNotes,
  deleteApplicationNote,
  setDeleteApplicationNote,
  setErrorDeleteApplicationNote,
  handleTabChangeAppDetailsSummary,
  handleGoBackAppDetailsSummary,
  fetchApplicantAttachmentUrl,
  setApplicantAttachmentUrl,
  setErrorApplicantAttachmentUrl,
  setDownloadApplicantAttachment,
  setErrorDownloadApplicantAttachment,
  downloadApplicantAttachment,
  updateApplicationProperty,
  setUpdateApplicationProperty,
  setErrorUpdateApplicationProperty,
  resetUpdateApplicationPropertyStates,
  exportApplicationZip,
  setErrorExportApplicationZip,
  setExportApplicationZip,
  clearExportApplicationDetailStates,
} = applicationsSlice.actions;

export default applicationsSlice.reducer;
export const selectApplicationsState = (state: RootState) => state.applicationsState;
